import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StashTab, Item, RATE_LIMIT, POE_AUTH_CONFIG, Idol } from '../../types';
import useFetchedIdols from 'hooks/useFetchedIdols';
import useLocalStorage from 'hooks/useLocalStorage';
import { convertItemToIdol } from '../../utils/idolParser';
import { getTextFromModId } from 'utils/mod-utils'


interface StashExplorerProps {
  accessToken: string | null;
  league: string;
  onError: (message: string) => void;
}

export const StashExplorer: React.FC<StashExplorerProps> = ({
  accessToken,
  league,
  onError
}) => {
  const [stashes, setStashes] = useState<StashTab[]>([]);
  const [selectedStashIds, setSelectedStashIds] = useState<string[]>([]);
  const [idols, setIdols] = useState<Idol[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingIdols, setIsFetchingIdols] = useState<boolean>(false);
  const [lastFetchTime, setLastFetchTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);

  const [savedStashIds, setSavedStashIds] = useLocalStorage<string[]>('selectedStashIds', []);
  const [savedLastFetchTime, setSavedLastFetchTime] = useLocalStorage<number | null>('lastIdolFetchTime', null);

  const [placedItems] = useLocalStorage<Idol[]>('idolPlannerItems', []);

  const lastFetchTimeRef = useRef<number>(0);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [stashesLoaded, setStashesLoaded] = useState<boolean>(false);

  const [stashFetchCooldown, setStashFetchCooldown] = useState<number>(0);
  const [isStashCoolingDown, setIsStashCoolingDown] = useState<boolean>(false);

  const COOLDOWN_PERIOD = 5 * 60 * 1000; // 5 minutes in ms
  const STASH_COOLDOWN_PERIOD = 5 * 60 * 1000; // 5 minutes in ms
  const MAX_SELECTED_TABS = 5;

  const { fetchedIdols, setFetchedIdols } = useFetchedIdols();

  // Calculate exponential backoff delay
  const getBackoffDelay = (retryCount: number): number => {
    return Math.min(
      RATE_LIMIT.RETRY_DELAY * Math.pow(2, retryCount),
      RATE_LIMIT.MAX_RETRY_DELAY
    );
  };

  // Clear any existing fetch timeouts
  const clearFetchTimeout = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
  }, []);

  // Update cooldown timer
  useEffect(() => {
    // Clear any existing interval
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }

    if (lastFetchTime) {
      const updateCooldown = () => {
        const elapsed = Date.now() - lastFetchTime;
        if (elapsed >= COOLDOWN_PERIOD) {
          setCooldownRemaining(0);
          if (cooldownIntervalRef.current) {
            clearInterval(cooldownIntervalRef.current);
            cooldownIntervalRef.current = null;
          }
        } else {
          setCooldownRemaining(Math.ceil((COOLDOWN_PERIOD - elapsed) / 1000));
        }
      };

      // Initial update
      updateCooldown();

      // Set interval to update every second
      cooldownIntervalRef.current = setInterval(updateCooldown, 1000);
    }

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [lastFetchTime, COOLDOWN_PERIOD]);

  useEffect(() => {
    if (savedLastFetchTime) {
      setLastFetchTime(savedLastFetchTime);
    }
  }, [savedLastFetchTime]);

  useEffect(() => {
    if (fetchedIdols.length > 0) {
      setIdols(fetchedIdols);
    }
  }, [fetchedIdols]);

  useEffect(() => {
    const lastFetchTime = localStorage.getItem('lastStashFetchTime');
    if (lastFetchTime) {
      const elapsed = Date.now() - parseInt(lastFetchTime);
      if (elapsed < STASH_COOLDOWN_PERIOD) {
        setIsStashCoolingDown(true);

        // Start cooldown timer
        const timer = setInterval(() => {
          const remaining = Math.ceil((STASH_COOLDOWN_PERIOD - (Date.now() - parseInt(lastFetchTime))) / 1000);
          if (remaining <= 0) {
            setIsStashCoolingDown(false);
            setStashFetchCooldown(0);
            clearInterval(timer);
          } else {
            setStashFetchCooldown(remaining);
          }
        }, 1000);

        return () => clearInterval(timer);
      }
    }
  }, [STASH_COOLDOWN_PERIOD]);


  useEffect(() => {
    // Load fetched idols from localStorage when component is mounted
    if (fetchedIdols.length > 0) {
      setIdols(fetchedIdols);
    } else {
      const savedIdols = localStorage.getItem('fetchedIdols');
      if (savedIdols) {
        try {
          const parsedIdols = JSON.parse(savedIdols);
          if (Array.isArray(parsedIdols) && parsedIdols.length > 0) {
            setIdols(parsedIdols);
            setFetchedIdols(parsedIdols);
          }
        } catch (e) {
          console.error('Error parsing saved idols:', e);
        }
      }
    }
  }, [fetchedIdols, setFetchedIdols]);

  useEffect(() => {
    if (stashes.length > 0 && savedStashIds.length > 0) {
      // Only include stash IDs that still exist
      const validIds = savedStashIds.filter(id =>
        stashes.some(stash => stash.id === id)
      );
      setSelectedStashIds(validIds);
    }
  }, [stashes, savedStashIds]);


  const handleFetchStashTabs = () => {
    if (isStashCoolingDown) {
      onError(`Please wait ${stashFetchCooldown} seconds before fetching stash tabs again.`);
      return;
    }

    if (!accessToken) return;

    fetchStashes(accessToken);
    setStashesLoaded(true);

    // Set cooldown
    setIsStashCoolingDown(true);
    const startTime = Date.now();
    localStorage.setItem('lastStashFetchTime', startTime.toString());

    // Start cooldown timer
    const timer = setInterval(() => {
      const remaining = Math.ceil((STASH_COOLDOWN_PERIOD - (Date.now() - startTime)) / 1000);
      if (remaining <= 0) {
        setIsStashCoolingDown(false);
        setStashFetchCooldown(0);
        clearInterval(timer);
      } else {
        setStashFetchCooldown(remaining);
      }
    }, 1000);
  };

  // Fetch stashes using the proxy endpoint
  const fetchStashes = useCallback(async (token: string, retryCount = 0): Promise<void> => {
    if (!token) return;

    // Rate limiting
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;

    if (timeSinceLastFetch < 2000 && retryCount === 0) {
      clearFetchTimeout();
      fetchTimeoutRef.current = setTimeout(() => {
        fetchStashes(token, retryCount);
      }, 2000 - timeSinceLastFetch);
      return;
    }

    setIsLoading(true);
    lastFetchTimeRef.current = now;

    try {
      const response = await fetch(`${POE_AUTH_CONFIG.stashApiUrl}/${league}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      // Handle rate limits
      if (response.status === 429) {
        const data = await response.json();
        const retryAfter = data.retryAfter || 5;

        if (retryCount < RATE_LIMIT.MAX_RETRIES) {
          const delay = getBackoffDelay(retryCount);

          onError(`Rate limit exceeded. Retrying in ${Math.ceil(delay / 1000)} seconds...`);

          clearFetchTimeout();
          fetchTimeoutRef.current = setTimeout(() => {
            fetchStashes(token, retryCount + 1);
          }, delay);

          return;
        } else {
          throw new Error(`Rate limit exceeded. Please try again in ${retryAfter} seconds.`);
        }
      }

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('poe_access_token');
          localStorage.removeItem('poe_username');
          localStorage.removeItem('poe_token_expires_at');
          throw new Error('Authentication expired. Please log in again.');
        } else {
          try {
            const errorData = await response.json();
            throw new Error(errorData.error || `API Error: ${response.status}`);
          } catch (jsonError) {
            const text = await response.text();
            throw new Error(`API Error (${response.status}): ${text.substring(0, 100)}`);
          }
        }
      }

      const data = await response.json();

      if (!data.stashes || !Array.isArray(data.stashes)) {
        throw new Error('Invalid response format from API');
      }

      setStashes(data.stashes);
      RATE_LIMIT.retryCount = 0; // Reset retry count on success
    } catch (err) {
      console.error('Stash fetch error:', err);
      onError(err instanceof Error ? err.message : 'Failed to fetch stashes');

      if (retryCount < RATE_LIMIT.MAX_RETRIES && !(err instanceof Error && err.message.includes('Rate limit'))) {
        const delay = getBackoffDelay(retryCount);

        clearFetchTimeout();
        fetchTimeoutRef.current = setTimeout(() => {
          fetchStashes(token, retryCount + 1);
        }, delay);
      }
    } finally {
      setIsLoading(false);
    }
  }, [league, clearFetchTimeout, onError]);


  // Fetch stash items for a specific stash
  const fetchStashItems = useCallback(async (token: string, stashId: string): Promise<Item[]> => {
    if (!token || !stashId) return [];

    try {
      const response = await fetch(`${POE_AUTH_CONFIG.stashApiUrl}/${league}/${stashId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch stash tab items: ${response.status}`);
      }

      const data = await response.json();

      if (!data.stash || !data.stash.items) {
        return [];
      }

      return data.stash.items;
    } catch (err) {
      console.error(`Error fetching items for stash ${stashId}:`, err);
      return [];
    }
  }, [league]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      clearFetchTimeout();
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, [clearFetchTimeout]);

  // Fetch stashes when token or league changes

  // Toggle stash selection
  const toggleStashSelection = (stashId: string) => {
    setSelectedStashIds((prev) => {
      const newSelection = prev.includes(stashId)
        ? prev.filter(id => id !== stashId)
        : (prev.length >= MAX_SELECTED_TABS
          ? prev
          : [...prev, stashId]);

      // Save to localStorage
      setSavedStashIds(newSelection);
      return newSelection;
    });
  };

  // Fetch idols from selected stashes
  const fetchIdols = async () => {
    if (!accessToken) return;

    // Check if on cooldown
    if (lastFetchTime && Date.now() - lastFetchTime < COOLDOWN_PERIOD) {
      const remainingSeconds = Math.ceil((COOLDOWN_PERIOD - (Date.now() - lastFetchTime)) / 1000);
      onError(`Please wait ${remainingSeconds} seconds before fetching idols again.`);
      return;
    }

    if (selectedStashIds.length === 0) {
      onError('Please select at least one stash tab.');
      return;
    }

    setIsFetchingIdols(true);
    setIdols([]);

    try {
      const allIdols: Idol[] = [];
      const errors: string[] = [];

      for (const stashId of selectedStashIds) {
        const stashTab = stashes.find(stash => stash.id === stashId);
        const stashName = stashTab?.name || "Unknown Stash";

        const items = await fetchStashItems(accessToken, stashId);

        const idolItems = items.filter(item =>
          item.baseType?.toLowerCase().includes('idol') ||
          item.typeLine?.toLowerCase().includes('idol')
        );

        // Convert PoE items to Idol objects
        for (const item of idolItems) {
          const { idol, error } = convertItemToIdol(item, stashName);
          if (idol) {
            allIdols.push(idol);
          } else if (error) {
            errors.push(error);
          }
        }
      }

      // Update state and localStorage
      setIdols(allIdols);
      setFetchedIdols(allIdols);

      if (errors.length > 0) {
        console.warn(`${errors.length} idols could not be imported:`, errors);
      }

      // Set last fetch time for cooldown
      setLastFetchTime(Date.now());

      setSavedLastFetchTime(Date.now());
    } catch (err) {
      console.error('Error fetching idols:', err);
      onError(err instanceof Error ? err.message : 'Failed to fetch idols');
    } finally {
      setIsFetchingIdols(false);
    }
  };

  // Determine stash color CSS class based on metadata
  const getStashColorClass = (stash: StashTab): string => {
    if (!stash.metadata.colour) return 'border-gray-600';

    const hex = stash.metadata.colour.toLowerCase();

    switch (hex) {
      case '#ff0000': return 'border-red-500';
      case '#00ff00': return 'border-green-500';
      case '#0000ff': return 'border-blue-500';
      case '#ffff00': return 'border-yellow-500';
      case '#00ffff': return 'border-cyan-500';
      case '#ff00ff': return 'border-purple-500';
      case '#ffffff': return 'border-white';
      default: {
        if (hex.startsWith('#f')) return 'border-red-400';
        if (hex.startsWith('#0')) return 'border-blue-400';
        if (hex.startsWith('#9')) return 'border-purple-400';
        if (hex.startsWith('#a')) return 'border-amber-400';
        if (hex.startsWith('#8')) return 'border-green-400';
        return 'border-gray-400';
      }
    }
  };

  const createIdolGroupsByStat = useCallback(() => {
    const statIdToIdolsMap: Record<number, Idol[]> = {};
    const statIdToDisplayText: Record<number, string> = {};
    const statIdToCountInPlanner: Record<number, number> = {};

    // First collect all unique mod IDs from planner items
    const uniquePlannerModIds = new Set<number>();
    placedItems.forEach(item => {
      item.stats.forEach(stat => {
        uniquePlannerModIds.add(stat.modId);
      });
    });

    // Initialize maps for all planner mods, even if we don't have matching idols
    uniquePlannerModIds.forEach(modId => {
      statIdToIdolsMap[modId] = [];
      statIdToCountInPlanner[modId] = 0;

      // Store the display text for this stat
      const displayText = getTextFromModId(modId);
      if (displayText) {
        statIdToDisplayText[modId] = displayText;
      } else {
        statIdToDisplayText[modId] = `Unknown Modifier (ID: ${modId})`;
      }
    });

    // Count occurrences of each mod in planner items
    placedItems.forEach(item => {
      item.stats.forEach(stat => {
        statIdToCountInPlanner[stat.modId]++;
      });
    });

    // Filter idols by search term before grouping
    const filteredIdols = searchTerm ? idols.filter(idol => {
      // Search in name
      if (idol.name && idol.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }

      // Search in mod descriptions
      return idol.stats.some(stat => {
        const modText = getTextFromModId(stat.modId);
        return modText && modText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }) : idols;

    // Group our stash idols by the stats found in planner
    filteredIdols.forEach(idol => {
      idol.stats.forEach(stat => {
        // Only add to groups if this stat is in the planner
        if (uniquePlannerModIds.has(stat.modId)) {
          // Avoid duplicates in the same group
          if (!statIdToIdolsMap[stat.modId].some(i => i.id === idol.id)) {
            statIdToIdolsMap[stat.modId].push(idol);
          }
        }
      });
    });

    // Add "All other idols" group for idols not matching any planner stats
    const groupedIdolIds = new Set<string>();
    Object.values(statIdToIdolsMap).forEach(idolList => {
      idolList.forEach(idol => groupedIdolIds.add(idol.id));
    });

    const ungroupedIdols = filteredIdols.filter(idol => !groupedIdolIds.has(idol.id));

    if (ungroupedIdols.length > 0) {
      statIdToIdolsMap[-1] = ungroupedIdols; // Use -1 as key for ungrouped idols
      statIdToDisplayText[-1] = "All other idols";
      statIdToCountInPlanner[-1] = 0;
    }

    return { statIdToIdolsMap, statIdToDisplayText, statIdToCountInPlanner };
  }, [idols, placedItems, searchTerm]);

  const renderIdols = () => {
    if (idols.length === 0) {
      return (
        <div className="text-center p-4 text-gray-400">
          No idols found in selected stash tabs.
        </div>
      );
    }

    const { statIdToIdolsMap, statIdToDisplayText, statIdToCountInPlanner } = createIdolGroupsByStat();

    // Separate groups into "planner mods" and "other mods"
    const plannerModIds = Object.keys(statIdToCountInPlanner).filter(id => id !== '-1');
    const otherModIds = statIdToIdolsMap[-1] ? ['-1'] : [];

    return (
      <div className="space-y-6">
        <h2 className="text-lg font-semibold border-b border-gray-700 pb-2 text-blue-400">
          Mods from Grid Planner
        </h2>

        {plannerModIds.map(statIdStr => {
          // For each planner mod, show it even if we have 0 matching idols
          const statId = parseInt(statIdStr);
          const displayText = statIdToDisplayText[statId] || "Unknown Modifier";
          const countInPlanner = statIdToCountInPlanner[statId] || 0;
          const groupIdols = statIdToIdolsMap[statId] || [];

          return (
            <div key={statIdStr} className="mb-6">
              <details className="group">
                <summary className="flex justify-between items-center cursor-pointer list-none py-2 px-4 bg-gray-700 rounded-md">
                  <h3 className="text-sm font-semibold text-blue-300">
                    {displayText} (
                    <span className={groupIdols.length < countInPlanner ? "text-red-500" : ""}>{groupIdols.length} in stash</span> / {countInPlanner} in planner)
                  </h3>
                  <div className="transform group-open:rotate-180">
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                </summary>

                {groupIdols.length > 0 ? (
                  <div className="mt-3 pl-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {groupIdols.map((idol, index) => (
                      <div
                        key={idol.id || index}
                        className="bg-gray-800 p-3 rounded border border-blue-400 hover:border-blue-400 transition duration-200"
                      >
                        <div className="font-semibold text-lg mb-1">{idol.name || "Unnamed Idol"}</div>
                        <div className="text-sm text-gray-300 mb-2">{idol.size.name}</div>

                        {/* Show stats */}
                        {idol.stats && idol.stats.length > 0 && (
                          <div className="text-xs text-blue-300 mt-2 border-t border-gray-700 pt-2 space-y-1">
                            {idol.stats.map((stat, statIndex) => {
                              if (!stat) return null;

                              // Cast to our extended stat type to access originalId
                              const extendedStat = stat;

                              let displayText = getTextFromModId(extendedStat.modId) || "Unknown modifier";

                              // Format the description with proper value
                              if (displayText) {
                                // Replace patterns like "(150–200)" or "(0.5–0.7)" with the actual value
                                displayText = displayText.replace(/(\+?\()(\d+(\.\d+)?)–(\d+(\.\d+)?)(\))/g, (match, prefix) => {
                                  // Keep the + if it exists in the prefix
                                  const plusSign = prefix.includes('+') ? '+' : '';
                                  // Check if the original match contained a '%' sign
                                  const hasPercentSign = match.includes('%');
                                  return `${plusSign}${extendedStat.value}${hasPercentSign ? '%' : ''}`; // Add % only if it was in the original text
                                });
                              }

                              // Determine text color based on prefix/suffix
                              let textColorClass = "text-blue-400"; // Default

                              // Check for originalId (should be available for newly created items)
                              if (extendedStat.explicitType) {
                                if (extendedStat.explicitType.startsWith('prefix')) {
                                  textColorClass = "text-[#ffa9ff]"; // Pink color for prefixes
                                } else if (extendedStat.explicitType.startsWith('suffix') ||
                                  extendedStat.explicitType.includes('suffix')) {
                                  textColorClass = "text-[#a9ffff]"; // Cyan color for suffixes
                                }
                              }

                              // Highlight the stat that matches this group
                              const isMatchingStat = parseInt(statIdStr) === extendedStat.modId;
                              const highlightClass = isMatchingStat ? "font-bold" : "opacity-60"; // Gray out non-matching mods

                              return (
                                <div key={statIndex} className={`${textColorClass} ${highlightClass} mb-1.5 text-sm`}>
                                  {displayText}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Show tab info at bottom */}
                        {idol.stashTabName && (
                          <div className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-700">
                            Tab: {idol.stashTabName} {idol.stashPosition && `(${idol.stashPosition.x},${idol.stashPosition.y})`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-3 pl-4 text-gray-400 italic">
                    No matching idols found in your stash tabs.
                  </div>
                )}
              </details>
            </div>
          );
        })}

        {otherModIds.length > 0 && (
          <>
            <h2 className="text-lg font-semibold border-b border-gray-700 pb-2 pt-4 text-gray-400">
              Other Idols
            </h2>

            {otherModIds.map(statIdStr => {
              const statId = parseInt(statIdStr);
              const displayText = statIdToDisplayText[statId] || "Unknown Modifier";
              const groupIdols = statIdToIdolsMap[statId] || [];

              return (
                <div key={statIdStr} className="mb-6">
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer list-none py-2 px-4 bg-gray-700 rounded-md">
                      <h3 className="text-sm font-semibold text-gray-300">
                        {displayText} ({groupIdols.length})
                      </h3>
                      <div className="transform group-open:rotate-180">
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </summary>

                    <div className="mt-3 pl-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {groupIdols.map((idol, index) => (
                        <div
                          key={idol.id || index}
                          className="bg-gray-800 p-3 rounded border border-gray-600 hover:border-gray-500 transition duration-200"
                        >
                          <div className="font-semibold text-lg mb-1">{idol.name || "Unnamed Idol"}</div>
                          <div className="text-sm text-gray-300 mb-2">{idol.size.name}</div>

                          {/* Show stats */}
                          {idol.stats && idol.stats.length > 0 && (
                            <div className="text-xs text-blue-300 mt-2 border-t border-gray-700 pt-2 space-y-1">
                              {idol.stats.map((stat, statIndex) => {
                                if (!stat) return null;

                                const extendedStat = stat;
                                let displayText = getTextFromModId(extendedStat.modId) || "Unknown modifier";

                                // Format the description with proper value
                                if (displayText) {
                                  displayText = displayText.replace(/(\+?\()(\d+(\.\d+)?)–(\d+(\.\d+)?)(\))/g, (match, prefix) => {
                                    const plusSign = prefix.includes('+') ? '+' : '';
                                    const hasPercentSign = match.includes('%');
                                    return `${plusSign}${extendedStat.value}${hasPercentSign ? '%' : ''}`;
                                  });
                                }

                                // Determine text color based on prefix/suffix
                                let textColorClass = "text-blue-400"; // Default

                                if (extendedStat.explicitType) {
                                  if (extendedStat.explicitType.startsWith('prefix')) {
                                    textColorClass = "text-[#ffa9ff]"; // Pink color for prefixes
                                  } else if (extendedStat.explicitType.startsWith('suffix') ||
                                    extendedStat.explicitType.includes('suffix')) {
                                    textColorClass = "text-[#a9ffff]"; // Cyan color for suffixes
                                  }
                                }

                                return (
                                  <div key={statIndex} className={`${textColorClass} mb-1.5 text-sm`}>
                                    {displayText}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Show tab info at bottom */}
                          {idol.stashTabName && (
                            <div className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-700">
                              Tab: {idol.stashTabName} {idol.stashPosition && `(${idol.stashPosition.x},${idol.stashPosition.y})`}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <div>
      {isLoading && (
        <div className="flex justify-center my-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Stash Tab Selection */}
        <div className="md:col-span-1 bg-gray-900 rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-medium mb-3">Stash Tabs</h3>
          <p className="text-sm text-gray-400 mb-4">
            Select up to {MAX_SELECTED_TABS} stash tabs to fetch idols from.
          </p>

          {!stashesLoaded && accessToken ? (
            <button
              onClick={handleFetchStashTabs}
              disabled={isStashCoolingDown}
              className={`w-full py-2 px-4 rounded ${isStashCoolingDown
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-500 text-white'
                } mb-4`}
            >
              {isStashCoolingDown
                ? `Wait ${stashFetchCooldown}s to fetch stash tabs`
                : 'Load Stash Tabs'}
            </button>
          ) : null}

          {stashes.length === 0 ? (
            <div className="text-center p-4 text-gray-400">
              No stash tabs found in {league} league.
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar pr-2">
              {stashes.map((stash) => (
                <div
                  key={stash.id}
                  className={`bg-gray-800 p-3 rounded-lg border 
                    ${getStashColorClass(stash)} 
                    hover:border-blue-400 transition duration-200 
                    ${selectedStashIds.includes(stash.id) ? 'border-blue-500 bg-gray-700' : ''}
                    ${stash.metadata.folder ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  onClick={() => !stash.metadata.folder && toggleStashSelection(stash.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{stash.name}</div>
                    {!stash.metadata.folder && (
                      <input
                        type="checkbox"
                        checked={selectedStashIds.includes(stash.id)}
                        onChange={() => toggleStashSelection(stash.id)}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Type: {stash.type}</div>
                  {stash.metadata.folder && (
                    <div className="text-xs text-blue-400 mt-1">Folder (cannot select folders)</div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={fetchIdols}
              disabled={isFetchingIdols || selectedStashIds.length === 0 || cooldownRemaining > 0}
              className={`w-full py-2 px-4 rounded ${isFetchingIdols || selectedStashIds.length === 0 || cooldownRemaining > 0
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-500 text-white'
                }`}
            >
              {isFetchingIdols ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Fetching Idols...
                </span>
              ) : cooldownRemaining > 0 ? (
                `Wait ${cooldownRemaining}s to fetch again`
              ) : (
                `Fetch Idols from ${selectedStashIds.length} tab${selectedStashIds.length !== 1 ? 's' : ''}`
              )}
            </button>

            {selectedStashIds.length > 0 && (
              <div className="mt-2 text-xs text-gray-400">
                Selected {selectedStashIds.length} of {MAX_SELECTED_TABS} tabs
              </div>
            )}
          </div>
        </div>

        {/* Idol Display */}
        <div className="md:col-span-3 bg-gray-900 rounded-lg p-4 border border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">
              Idol Collection {fetchedIdols.length}
            </h3>
            <div className="w-1/2">
              <input
                type="text"
                placeholder="Search by name or modifier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {isFetchingIdols ? (
            <div className="flex justify-center my-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
              {renderIdols()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StashExplorer;